import { api } from '@/services/api';
import { handleError } from '@/services/error';
import { ApiResponse } from '@/types/api';
import { CreateUser, AdminCreateUserData, UpdateUserInfo, UserInfo, UpdateAdminUserInfoData, AdminUser } from '@/types/user';
import axios from 'axios';

// (auth)/register/page.tsx
// ユーザー登録
export const register = async (
  userData: CreateUser
): Promise<ApiResponse> => {
  try {
    const response = await api.post<{ success: boolean; message: string }>('/auth/register/', userData);
    return {
      success: true,
      data: response.data,
      message: response.data.message || 'ユーザーが正常に登録されました'
    };
  } catch (error) {
    console.error('ユーザー登録エラー:', error);
    if (axios.isAxiosError(error)) {
      if (error.response) {
        // サーバーからのエラーレスポンス
        throw handleError(error, 'USER_CREATE_ERROR');
      } else if (error.request) {
        // リクエストは送信されたがレスポンスがない
        throw handleError(error, 'REQUEST_FAILED');
      }
    }
    // その他のエラー
    throw handleError(error, 'UNKNOWN_ERROR');
  }
};

// (auth)/register/page.tsx
// ユーザー名の重複チェック
export const checkUsernameExists = async (
  username: string
): Promise<boolean> => {
  try {
    const response = await api.post<{ exists: boolean }>('/auth/check-username/', { username });
    return response.data.exists;
  } catch (error) {
    console.error('ユーザー名重複チェックエラー:', error);
    if (axios.isAxiosError(error)) {
      if (error.response) {
        // サーバーからのエラーレスポンス
        const message = error.response.data.error || 'ユーザー名の確認中にエラーが発生しました';
        throw handleError(new Error(message), 'BAD_REQUEST');
      } else if (error.request) {
        // リクエストは送信されたがレスポンスがない
        throw handleError(error, 'REQUEST_FAILED');
      }
    }
    // その他のエラー
    throw handleError(new Error('ネットワークエラーが発生しました'), 'NETWORK_ERROR');
  }
};

// (auth)/register/page.tsx
// メールアドレスの重複チェック
export const checkEmailExists = async (
  email: string
): Promise<boolean> => {
  try {
    const response = await api.post<{ exists: boolean }>('/auth/check-email/', { email });
    return response.data.exists;
  } catch (error) {
    console.error('メールアドレス重複チェックエラー:', error);
    if (axios.isAxiosError(error)) {
      if (error.response) {
        // サーバーからのエラーレスポンス
        const message = error.response.data.error || 'メールアドレスの確認中にエラーが発生しました';
        throw handleError(new Error(message), 'BAD_REQUEST');
      } else if (error.request) {
        // リクエストは送信されたがレスポンスがない
        throw handleError(error, 'REQUEST_FAILED');
      }
    }
    // その他のエラー
    throw handleError(new Error('ネットワークエラーが発生しました'), 'NETWORK_ERROR');
  }
};

// (auth)/password-reset/page.tsx
// パスワードリセットメールの送信
export const sendPasswordResetEmail = async (
  email: string
): Promise<ApiResponse> => {
  try {
    const response = await api.post('/auth/password-reset/', { email });
    return { success: true, data: response.data };
  } catch (error) {
    throw handleError(error, 'BAD_REQUEST');
  }
};

// (auth)/password-reset/page.tsx
// パスワードのリセット
export const resetPassword = async (
  token: string,
  newPassword: string
): Promise<ApiResponse> => {
  try {
    const response = await api.post('/auth/password-reset/confirm/', {
      token,
      new_password: newPassword
    });
    return { success: true, data: response.data };
  } catch (error) {
    throw handleError(error, 'BAD_REQUEST');
  }
};

// user/profile/page.tsx
// user/profile/edit/page.tsx
// ユーザー情報の取得
export const getUserInfo = async (): Promise<ApiResponse<UserInfo>> => {
  try {
    const response = await api.get('/user/');
    return { success: true, data: response.data };
  } catch (error) {
    throw handleError(error, 'REQUEST_FAILED');
  }
};

// user/profile/edit/page.tsx
// ユーザー情報の更新
export const updateUserInfo = async (updateData: UpdateUserInfo): Promise<ApiResponse<UserInfo>> => {
  try {
    const response = await api.put('/user/update/', updateData);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    return handleError<UserInfo>(error, 'USER_UPDATE_ERROR');
  }
};

// user/profile/edit/page.tsx
// パスワードの変更
export const changePassword = async (
  oldPassword: string,
  newPassword: string
): Promise<ApiResponse> => {
  try {
    const response = await api.post('/auth/password/change/', {
      old_password: oldPassword,
      new_password: newPassword
    });
    return { success: true, data: response.data };
  } catch (error) {
    throw handleError(error, 'BAD_REQUEST');
  }
};

// 管理者用 ///////////////////////////////////////////////////////

// admin/users/page.tsx
// 全ユーザー情報の取得（管理者用）
export const getAllUsers = async (): Promise<ApiResponse<AdminUser[]>> => {
  try {
    const response = await api.get('/admin/users/');
    return { success: true, data: response.data };
  } catch (error) {
    throw handleError(error, 'REQUEST_FAILED');
  }
};

// admin/users/page.tsx
// 管理者用ユーザー作成
export const createUser = async (
  userData: AdminCreateUserData
): Promise<ApiResponse> => {
  try {
    const response = await api.post('/admin/users/create/', userData);
    return {
      success: true,
      data: response.data.data,
      message: response.data.message
    };
  } catch (error) {
    throw handleError(error, 'USER_CREATE_ERROR');
  }
};

// admin/users/page.tsx
// 管理者用ユーザー情報の取得
export const getAdminUserInfo = async (
  userId: string
): Promise<ApiResponse> => {
  try {
    const response = await api.get(`/admin/users/${userId}/`);
    return {
      success: true,
      message: 'ユーザー情報を取得しました',
      data: response.data
    };
  } catch (error) {
    throw handleError(error, 'REQUEST_FAILED');
  }
};

// admin/users/page.tsx
// 管理者用ユーザー情報の更新
export const updateAdminUserInfo = async (
  userId: number,
  userData: UpdateAdminUserInfoData
): Promise<ApiResponse> => {
  try {
    const response = await api.put(`/admin/users/${userId}/update/`, userData);
    return {
      success: true,
      message: 'ユーザー情報が正常に更新されました',
      data: response.data
    };
  } catch (error) {
    throw handleError(error, 'REQUEST_FAILED');
  }
};
